import { useRef, useState, useEffect, useCallback } from 'react';
import { Package, ClipboardCheck, Truck, MapPin, CheckCircle2 } from 'lucide-react';

/**
 * Ruta del pedido: seis paradas fijas, tomadas literalmente de status_map en
 * server.py (admin_update_order_status). No es una librería de "stepper"
 * genérica — es el mismo vocabulario de estados que ya usa el admin para
 * mover un pedido, mostrado como una línea de tiempo hacia adelante en vez
 * de hacia atrás (que es lo que ya hace TrackingTimeline con los eventos).
 *
 * Deliberadamente NO promete tiempos ("llega en 2 días"): el sistema no
 * tiene esa información real, y prometerla sería inventar un dato — mismo
 * principio que ya rige highlights.js (bestPricePerKgId) y StatsBand. Lo que
 * SÍ es cien por ciento cierto es la posición en la secuencia, así que eso es
 * lo único que se comunica: dónde está y cuál es el siguiente paso.
 */
const STAGES = [
    { key: 'confirmado', label: 'Confirmado', Icon: Package },
    { key: 'en_preparacion', label: 'En Preparación', Icon: ClipboardCheck },
    { key: 'enviado', label: 'Enviado', Icon: Truck },
    { key: 'en_transito', label: 'En Tránsito', Icon: Truck },
    { key: 'en_reparto', label: 'En Reparto', Icon: MapPin },
    { key: 'entregado', label: 'Entregado', Icon: CheckCircle2 },
];

function stageIndex(status) {
    return STAGES.findIndex((s) => s.key === status);
}

/**
 * Variante compacta: fila de puntos para una tarjeta de lista (Mi Negocio,
 * historial). No lleva iconos ni etiquetas — es un vistazo, no un detalle;
 * la etiqueta de texto (Badge) que ya vive junto a ella sigue siendo la
 * fuente accesible del estado.
 */
export function OrderJourneyDots({ status, className = '' }) {
    const idx = stageIndex(status);
    return (
        <div className={`flex items-center gap-1 ${className}`} aria-hidden="true" data-testid="order-journey-dots">
            {STAGES.map((stage, i) => {
                const done = idx >= 0 && i <= idx;
                const current = i === idx;
                return (
                    <span
                        key={stage.key}
                        className="rounded-full flex-shrink-0"
                        style={{
                            width: current ? '7px' : '5px',
                            height: current ? '7px' : '5px',
                            backgroundColor: done ? 'var(--kraft-deep)' : 'var(--line)',
                        }}
                    />
                );
            })}
        </div>
    );
}

/**
 * Variante completa: la ruta entera con iconos, etiquetas y el "siguiente
 * paso" explícito debajo. Para la página de detalle de pedido y el rastreo
 * público — el lugar donde alguien realmente se pregunta "¿y ahora qué
 * sigue?" en vez de solo "¿va bien?".
 */
export default function OrderJourney({ status }) {
    const idx = stageIndex(status);
    const current = idx >= 0 ? STAGES[idx] : null;
    const next = idx >= 0 && idx < STAGES.length - 1 ? STAGES[idx + 1] : null;
    const delivered = current?.key === 'entregado';

    // Los 6 pasos no caben en una pantalla angosta (504px de contenido mínimo
    // contra ~320-350px de ancho útil en móvil), así que la fila se desliza
    // horizontalmente — comprobado en el propio dispositivo: sin ninguna
    // señal, "En Reparto" y "Entregado" quedaban fuera de vista y nada en la
    // pantalla insinuaba que había más que ver. Estos dos degradados son esa
    // señal: aparecen solo cuando de verdad hay contenido oculto de ese lado
    // (no son decorativos fijos, que recortarían "Entregado" incluso cuando
    // los 6 pasos ya caben completos en pantallas anchas).
    const scrollRef = useRef(null);
    const currentRef = useRef(null);
    const [scrollState, setScrollState] = useState({ left: false, right: false });

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setScrollState({
            left: el.scrollLeft > 2,
            right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
        });
    }, []);

    useEffect(() => {
        // Centra el paso ACTUAL en móvil al montar: sin esto, la fila siempre
        // arranca mostrando "Confirmado" (el paso menos interesante, porque ya
        // pasó) y empuja fuera de vista justo lo que el usuario vino a ver —
        // dónde está su pedido ahora mismo. `inline: 'center'` no hace nada si
        // los 6 pasos ya caben enteros (no hay hacia dónde centrar), así que es
        // inofensivo en desktop.
        currentRef.current?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'instant' });
        updateScrollState();
        const el = scrollRef.current;
        if (!el) return undefined;
        el.addEventListener('scroll', updateScrollState, { passive: true });
        window.addEventListener('resize', updateScrollState);
        return () => {
            el.removeEventListener('scroll', updateScrollState);
            window.removeEventListener('resize', updateScrollState);
        };
    }, [updateScrollState, status]);

    return (
        <div data-testid="order-journey">
            {/* Fila de paradas. `overflow-x-auto` + `min-w-max` en vez de que
                el flex se comprima: en una pantalla angosta es mejor deslizar
                los 6 pasos que aplastarles el texto hasta ser ilegible. */}
            <div className="relative">
                {scrollState.left && (
                    <div aria-hidden="true" className="absolute left-0 top-0 bottom-1 w-8 pointer-events-none z-10"
                        style={{ background: 'linear-gradient(to right, var(--paper-2), transparent)' }} />
                )}
                {scrollState.right && (
                    <div aria-hidden="true" className="absolute right-0 top-0 bottom-1 w-8 pointer-events-none z-10"
                        style={{ background: 'linear-gradient(to left, var(--paper-2), transparent)' }} />
                )}
                <div ref={scrollRef} className="overflow-x-auto -mx-1 px-1 pb-1">
                    <div className="flex items-start min-w-max">
                    {STAGES.map((stage, i) => {
                        const done = idx >= 0 && i <= idx;
                        const isCurrent = i === idx;
                        const Icon = stage.Icon;
                        return (
                            <div key={stage.key} ref={isCurrent ? currentRef : null} className="flex items-center">
                                <div className="flex flex-col items-center" style={{ width: '84px' }}>
                                    <div
                                        className="relative rounded-full flex items-center justify-center flex-shrink-0"
                                        style={{
                                            width: isCurrent ? '40px' : '32px',
                                            height: isCurrent ? '40px' : '32px',
                                            backgroundColor: done ? 'var(--kraft-deep)' : 'transparent',
                                            border: done ? 'none' : '2px solid var(--line)',
                                            transition: 'width var(--dur-base) var(--ease-out-expo), height var(--dur-base) var(--ease-out-expo)',
                                        }}
                                    >
                                        {/* El paso actual respira con un anillo que
                                            se expande y desvanece — la única
                                            animación continua del componente, y
                                            se apaga entera bajo reduced-motion en
                                            vez de solo "reducirse", porque no
                                            comunica nada que el color ya no diga. */}
                                        {isCurrent && (
                                            <span
                                                aria-hidden="true"
                                                className="absolute inset-0 rounded-full motion-safe:animate-ping"
                                                style={{ backgroundColor: 'var(--kraft-deep)', opacity: 0.35 }}
                                            />
                                        )}
                                        <Icon
                                            className="relative"
                                            style={{
                                                width: isCurrent ? '18px' : '14px',
                                                height: isCurrent ? '18px' : '14px',
                                                color: done ? 'var(--paper)' : 'var(--ink-dim)',
                                            }}
                                        />
                                    </div>
                                    <p
                                        className="font-mono text-center mt-2 leading-tight"
                                        style={{
                                            fontSize: '9px',
                                            letterSpacing: '0.06em',
                                            textTransform: 'uppercase',
                                            color: isCurrent ? 'var(--ink)' : done ? 'var(--ink-soft)' : 'var(--ink-dim)',
                                            fontWeight: isCurrent ? 700 : 400,
                                        }}
                                    >
                                        {stage.label}
                                    </p>
                                </div>
                                {/* Conector perforado entre paradas — mismo lenguaje
                                    visual que PerforatedDivider, pero horizontal y
                                    corto: es la ruta, no una sección del sitio. */}
                                {i < STAGES.length - 1 && (
                                    <div
                                        aria-hidden="true"
                                        className="flex-shrink-0"
                                        style={{
                                            width: '28px',
                                            height: '2px',
                                            marginTop: isCurrent ? '19px' : i === idx - 1 ? '19px' : '15px',
                                            backgroundImage: `radial-gradient(circle, ${done && i < idx ? 'var(--kraft-deep)' : 'var(--line)'} 1.2px, transparent 1.3px)`,
                                            backgroundSize: '6px 2px',
                                            backgroundRepeat: 'repeat-x',
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })}
                    </div>
                </div>
            </div>

            {/* Siguiente paso: la única frase que este componente afirma sin
                reservas, porque es la posición en la secuencia, no una
                promesa de tiempo. */}
            <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--line)' }}>
                {delivered ? (
                    <p className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--success)' }} data-testid="order-journey-next">
                        <CheckCircle2 className="w-4 h-4" /> Pedido entregado
                    </p>
                ) : next ? (
                    <p className="text-sm" style={{ color: 'var(--ink-soft)' }} data-testid="order-journey-next">
                        Siguiente paso:{' '}
                        <span className="font-bold" style={{ color: 'var(--ink)' }}>{next.label}</span>
                    </p>
                ) : (
                    <p className="text-sm" style={{ color: 'var(--ink-soft)' }} data-testid="order-journey-next">
                        Actualizando estado del pedido...
                    </p>
                )}
            </div>
        </div>
    );
}
